'use client';

import React, { useState, useEffect } from 'react';
import { Search, Grid3X3, List, Map, Columns, ChevronDown, CalendarCheck } from 'lucide-react';
import { t } from '@/locales';

export type ViewMode = 'grid' | 'list' | 'map' | 'split';
export type SortOption = 'price_asc' | 'price_desc' | 'rating' | 'reviews' | 'newest' | 'name';

interface CatalogToolbarProps {
  totalResults: number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onToggleFilters?: () => void;
  showFiltersButton?: boolean;
  /** Toggle "Solo disponibili" — visibile sempre, attivabile solo con date range. */
  availableOnly?: boolean;
  onAvailableOnlyChange?: (next: boolean) => void;
  hasDateRange?: boolean;
  /** Callback quando user attiva chip senza date — apre/scrolla al date picker. */
  onRequestDates?: () => void;
}

const getSortOptions = (): { value: SortOption; label: string }[] => [
  { value: 'price_asc', label: t('catalog.sortPriceAsc') },
  { value: 'price_desc', label: t('catalog.sortPriceDesc') },
  { value: 'rating', label: t('catalog.sortRating') },
  { value: 'reviews', label: t('catalog.sortReviews') },
  { value: 'newest', label: t('catalog.sortNewest') },
  { value: 'name', label: t('catalog.sortName') },
];

const CatalogToolbar: React.FC<CatalogToolbarProps> = ({
  totalResults,
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
  onToggleFilters,
  showFiltersButton = false,
  availableOnly = false,
  onAvailableOnlyChange,
  hasDateRange = false,
  onRequestDates,
}) => {
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [sortOpen, setSortOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, onSearchChange]);

  const sortOptions = getSortOptions();
  const currentSortLabel = sortOptions.find(o => o.value === sortBy)?.label || t('catalog.sortDefault');

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.12)]" data-cid="boats-catalog-toolbar">
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4" data-cid="boats-catalog-toolbar-row">
        {/* Mobile filters button */}
        {showFiltersButton && (
          <button
            onClick={onToggleFilters}
            className="lg:hidden flex items-center justify-center gap-2 px-4 py-3 bg-slate-950 text-white rounded-xl font-semibold text-sm hover:bg-slate-800 transition-colors active:translate-y-px"
          >
            <Grid3X3 className="w-4 h-4" />
            {t('catalog.filters')}
          </button>
        )}

        {/* Toggle "Solo disponibili" — chip sempre attivabile.
            Senza range date il filtro è inerte (backend richiede from/to), ma toggle persiste:
            quando user setta date il filtro si applica automatic. */}
        {onAvailableOnlyChange && (
          <button
            type="button"
            data-cid="boats-catalog-toolbar-available-only"
            onClick={() => {
              const next = !availableOnly;
              onAvailableOnlyChange(next);
              // Click ON senza range date → trigga il date picker via parent
              if (next && !hasDateRange && onRequestDates) onRequestDates();
            }}
            title={hasDateRange ? 'Mostra solo barche disponibili nelle date scelte' : 'Click per scegliere le date'}
            className={`flex items-center gap-2 px-3 py-3 rounded-xl font-bold text-xs uppercase tracking-wider transition-all border-2 ${
              availableOnly
                ? 'bg-emerald-50 border-emerald-400 text-emerald-700 shadow-sm'
                : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300 hover:bg-emerald-50/40'
            } ${!hasDateRange && availableOnly ? 'opacity-70' : ''}`}
          >
            <CalendarCheck className="w-4 h-4" />
            <span className="hidden sm:inline">Solo disponibili</span>
            {!hasDateRange && availableOnly && (
              <span className="hidden lg:inline text-[9px] font-medium text-emerald-600/70 normal-case">(scegli date)</span>
            )}
          </button>
        )}

        {/* Search input */}
        <div className="relative flex-1 min-w-0" data-cid="boats-catalog-toolbar-search">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder={t('catalog.searchPlaceholder')}
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
          />
        </div>

        {/* Sort dropdown */}
        <div className="relative" data-cid="boats-catalog-toolbar-sort">
          <button
            onClick={() => setSortOpen(!sortOpen)}
            className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium text-sm hover:bg-slate-100 transition-colors min-w-[180px]"
          >
            <span className="truncate">{currentSortLabel}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
          </button>

          {sortOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setSortOpen(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-20" data-cid="boats-catalog-toolbar-sort-menu">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onSortChange(option.value);
                      setSortOpen(false);
                    }}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                      sortBy === option.value
                        ? 'bg-blue-50 text-blue-700 font-semibold'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* View mode toggle */}
        <div className="flex items-center bg-slate-100 rounded-xl p-1" data-cid="boats-catalog-toolbar-viewmode">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`p-2.5 rounded-lg transition-all ${
              viewMode === 'grid'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            title={t('catalog.viewGrid')}
          >
            <Grid3X3 className="w-5 h-5" />
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`p-2.5 rounded-lg transition-all ${
              viewMode === 'list'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            title={t('catalog.viewList')}
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={() => onViewModeChange('map')}
            className={`p-2.5 rounded-lg transition-all ${
              viewMode === 'map'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            title={t('catalog.viewMap')}
          >
            <Map className="w-5 h-5" />
          </button>
        </div>

        {/* Results count */}
        <div className="text-sm text-slate-500 font-medium whitespace-nowrap" data-cid="boats-catalog-toolbar-results-count">
          <span className="text-blue-600 font-bold">{totalResults}</span>{' '}
          {totalResults === 1 ? t('catalog.boatFound') : t('catalog.boatsFound')}
        </div>
      </div>
    </div>
  );
};

export default CatalogToolbar;
